import { useTRPC } from "@nwga/features/client";
import { Button } from "@nwga/ui/components/button";
import { Skeleton } from "@nwga/ui/components/skeleton";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { BuildingIcon } from "lucide-react";
import { toast } from "sonner";
import { AuthCard } from "../components/auth-card.tsx";

/**
 * Accepting an invitation.
 *
 * Public, because the visitor almost certainly has no account yet — that is the
 * whole reason invitations exist. The page therefore has to do three things in
 * order: say who is inviting them and to what, get them signed in, and only
 * then accept. Asking for a sign-up *before* telling them what it is for is how
 * an invitation link gets abandoned.
 *
 * The token stays in the URL through the sign-up round trip, carried as the
 * `redirect` search param, so the accept happens automatically on return.
 */
export const Route = createFileRoute("/invitations/$token")({
	component: AcceptInvitationPage,
});

function AcceptInvitationPage() {
	const { token } = Route.useParams();
	const trpc = useTRPC();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const invitation = useQuery(
		trpc.businesses.invitations.preview.queryOptions({ token }),
	);
	const session = useQuery(trpc.session.queryOptions());

	const accept = useMutation(
		trpc.businesses.invitations.accept.mutationOptions({
			onSuccess: async ({ business }) => {
				// The actor's memberships changed, so every permission-derived
				// affordance in the app is now stale.
				await queryClient.invalidateQueries();
				toast.success(`You've joined ${business.name}`);
				await navigate({
					to: "/dashboard/$businessSlug",
					params: { businessSlug: business.slug },
				});
			},
			onError: (error) => toast.error(error.message),
		}),
	);

	if (invitation.isPending || session.isPending || session.data === undefined) {
		return (
			<AuthCard title="Checking your invitation…">
				<Skeleton className="h-24 w-full" />
			</AuthCard>
		);
	}

	if (invitation.isError) {
		return (
			<AuthCard
				title="We can't find that invitation"
				description="The link may have been mistyped, or the business may have withdrawn it."
			>
				<Button asChild className="w-full">
					<Link to="/jobs">Go to the job board</Link>
				</Button>
			</AuthCard>
		);
	}

	const { businessName, email, role, state } = invitation.data;

	if (state !== "pending") {
		const explanation = {
			accepted:
				"This invitation has already been used. If that was you, it's in your dashboard.",
			revoked: `${businessName} withdrew this invitation. Ask them for a new one.`,
			expired: `This invitation has expired. Ask ${businessName} to send another.`,
		}[state];

		return (
			<AuthCard
				title="This invitation is no longer usable"
				description={explanation}
			>
				<Button asChild className="w-full">
					<Link to={session.data.user === null ? "/sign-in" : "/dashboard"}>
						{session.data.user === null ? "Sign in" : "Your businesses"}
					</Link>
				</Button>
			</AuthCard>
		);
	}

	const user = session.data.user;
	const roleLabel = role === "member" ? "a member" : `an ${role}`;

	// Signed out: show what the invitation is, then send them to sign up with the
	// token preserved so they land back here afterwards.
	if (user === null) {
		return (
			<AuthCard
				title={`Join ${businessName}`}
				description={`You've been invited to help manage ${businessName} on NWGA Connect as ${roleLabel}.`}
				footer={
					<>
						Already have an account?{" "}
						<Link
							to="/sign-in"
							search={{ redirect: `/invitations/${token}` }}
							className="font-medium text-foreground underline-offset-4 hover:underline"
						>
							Sign in
						</Link>
					</>
				}
			>
				<div className="space-y-4">
					<InvitationSummary
						businessName={businessName}
						email={email}
						roleLabel={roleLabel}
					/>
					<Button asChild className="w-full">
						<Link to="/sign-up" search={{ redirect: `/invitations/${token}` }}>
							Create an account to accept
						</Link>
					</Button>
				</div>
			</AuthCard>
		);
	}

	// Signed in as somebody else. The server refuses this anyway; saying so here
	// means they do not have to submit to find out.
	const wrongAccount =
		user.email.trim().toLowerCase() !== email.trim().toLowerCase();

	if (wrongAccount) {
		return (
			<AuthCard
				title="This invitation is for a different address"
				description={`It was sent to ${email}, but you're signed in as ${user.email}.`}
			>
				<div className="space-y-3">
					<InvitationSummary
						businessName={businessName}
						email={email}
						roleLabel={roleLabel}
					/>
					<p className="text-muted-foreground text-sm">
						Sign out and sign back in as {email} to accept it.
					</p>
					<Button asChild variant="outline" className="w-full">
						<Link to="/dashboard">Back to your businesses</Link>
					</Button>
				</div>
			</AuthCard>
		);
	}

	return (
		<AuthCard
			title={`Join ${businessName}`}
			description={`You've been invited to help manage ${businessName} as ${roleLabel}.`}
		>
			<div className="space-y-4">
				<InvitationSummary
					businessName={businessName}
					email={email}
					roleLabel={roleLabel}
				/>
				<Button
					className="w-full"
					disabled={accept.isPending}
					onClick={() => accept.mutate({ token })}
				>
					{accept.isPending ? "Joining…" : `Join ${businessName}`}
				</Button>
				<Button asChild variant="ghost" className="w-full">
					<Link to="/jobs">No thanks</Link>
				</Button>
			</div>
		</AuthCard>
	);
}

function InvitationSummary({
	businessName,
	email,
	roleLabel,
}: {
	readonly businessName: string;
	readonly email: string;
	readonly roleLabel: string;
}) {
	return (
		<div className="flex items-start gap-3 rounded-lg border border-dashed p-4">
			<BuildingIcon
				className="mt-0.5 size-5 shrink-0 text-muted-foreground"
				aria-hidden="true"
			/>
			<dl className="min-w-0 space-y-1 text-sm">
				<div className="flex gap-2">
					<dt className="text-muted-foreground">Business</dt>
					<dd className="truncate font-medium">{businessName}</dd>
				</div>
				<div className="flex gap-2">
					<dt className="text-muted-foreground">Your role</dt>
					<dd className="font-medium">{roleLabel}</dd>
				</div>
				<div className="flex gap-2">
					<dt className="text-muted-foreground">Invited</dt>
					<dd className="truncate font-medium">{email}</dd>
				</div>
			</dl>
		</div>
	);
}
